import type { IconName } from "@bundle/tokens";
import { useEffect, useId, useLayoutEffect, useRef } from "react";
import { Toaster as SonnerToaster, toast as sonner } from "sonner";
import { Button } from "./Button.tsx";
import { Icon } from "./Icon.tsx";
import { IconButton } from "./IconButton.tsx";

type ToastAction = { label: string; onPress: () => void; testID: string };

export type ToastProps = {
  open: boolean;
  /** The user dismissed it (close button, swipe), or it timed out. */
  onClose: () => void;
  message: string;
  /** A second line under the message, e.g. "Installs when you're away." */
  description?: string;
  /** Leading glyph: `accent` for updates, `success` for confirmations; absent = text only. */
  icon?: { name: IconName; tone?: "accent" | "success" };
  /** The main action, e.g. "Reload to update". */
  action?: ToastAction;
  /** A quieter second action, e.g. "Later". */
  secondaryAction?: ToastAction;
  /** Translated label of the close button. */
  closeLabel: string;
  /** Auto-dismiss after this many ms; absent = stays until acted on (never interrupts typing: it takes no focus). */
  durationMs?: number;
  testID?: string;
};

/**
 * Small, non-modal notification (design: "Toasts" pass, approved 2026-11-03). Web: shadcn's toast, i.e. Sonner,
 * stacked bottom right in the one `<Toaster>` the app mounts; iOS: glass banner overlay; Android: Snackbar. Never
 * steals focus, so it can't interrupt someone mid-typing; hovering the stack pauses the timers.
 *
 * Declarative: while `open`, the toast is shown (and updated in place when its text or actions change).
 */
export function Toast(props: ToastProps) {
  const baseId = useId();
  const latest = useRef(props);
  useLayoutEffect(() => {
    latest.current = props;
  });
  /**
   * The Sonner id of the toast on screen. Each opening gets a fresh one: Sonner dismisses on the next animation frame,
   * so a late dismiss of the last one must not remove a toast that was opened again right after.
   */
  const shown = useRef<string | undefined>(undefined);
  const seq = useRef(0);
  const { open, message, description, closeLabel, durationMs, testID } = props;
  // Primitives, so a parent passing fresh objects each render doesn't re-send the toast.
  const iconName = props.icon?.name;
  const iconTone = props.icon?.tone;
  const actionLabel = props.action?.label;
  const actionTestID = props.action?.testID;
  const secondaryLabel = props.secondaryAction?.label;
  const secondaryTestID = props.secondaryAction?.testID;

  useEffect(() => {
    if (!open) {
      const id = shown.current;
      shown.current = undefined;
      if (id !== undefined) sonner.dismiss(id);
      return;
    }
    if (shown.current === undefined) {
      seq.current += 1;
      shown.current = `${baseId}-${seq.current}`;
    }
    const id = shown.current;
    // Closed by the person (close button, swipe) or timed out; not when we closed it ourselves.
    const close = () => {
      if (shown.current !== id) return;
      shown.current = undefined;
      latest.current.onClose();
    };
    sonner.custom(
      (tid) => (
        <ToastCard
          message={message}
          description={description}
          icon={iconName === undefined ? undefined : { name: iconName, tone: iconTone }}
          action={actionLabel === undefined || actionTestID === undefined ? undefined : { label: actionLabel, testID: actionTestID, onPress: () => latest.current.action?.onPress() }}
          secondaryAction={
            secondaryLabel === undefined || secondaryTestID === undefined
              ? undefined
              : { label: secondaryLabel, testID: secondaryTestID, onPress: () => latest.current.secondaryAction?.onPress() }
          }
          closeLabel={closeLabel}
          testID={testID}
          onClose={() => sonner.dismiss(tid)}
        />
      ),
      { id, duration: durationMs ?? Number.POSITIVE_INFINITY, onDismiss: close, onAutoClose: close },
    );
  }, [baseId, open, message, description, iconName, iconTone, actionLabel, actionTestID, secondaryLabel, secondaryTestID, closeLabel, durationMs, testID]);

  useEffect(
    () => () => {
      const id = shown.current;
      shown.current = undefined;
      if (id !== undefined) sonner.dismiss(id);
    },
    [],
  );

  return null;
}

function ToastCard({
  message,
  description,
  icon,
  action,
  secondaryAction,
  closeLabel,
  testID,
  onClose,
}: Omit<ToastProps, "open" | "durationMs"> & { onClose: () => void }) {
  const stacked = description !== undefined && (action !== undefined || secondaryAction !== undefined);
  return (
    <div className="b-toast" data-testid={testID} data-layout={stacked ? "stacked" : "inline"}>
      {icon === undefined ? null : (
        <span className="b-toast-icon" data-tone={icon.tone ?? "accent"}>
          <Icon name={icon.name} size="icon.md" />
        </span>
      )}
      <div className="b-toast-text">
        <p className="b-toast-message">{message}</p>
        {description === undefined ? null : <p className="b-toast-description">{description}</p>}
      </div>
      {action === undefined && secondaryAction === undefined ? null : (
        <div className="b-toast-actions">
          {action === undefined ? null : <Button label={action.label} variant="primary" size="sm" onPress={action.onPress} testID={action.testID} />}
          {secondaryAction === undefined ? null : (
            <Button label={secondaryAction.label} variant="secondary" size="sm" onPress={secondaryAction.onPress} testID={secondaryAction.testID} />
          )}
        </div>
      )}
      <span className="b-toast-close">
        <IconButton icon="close" accessibilityLabel={closeLabel} size="sm" onPress={onClose} testID={`${testID ?? "toast"}-close`} />
      </span>
    </div>
  );
}

/**
 * Where toasts show: mount once at the app root, inside the I18nProvider. Bottom right, 16px in, up to three
 * stacked (they fan out on hover); full width at the bottom on narrow windows.
 */
export function Toaster({ label }: { label: string }) {
  return (
    <SonnerToaster
      position="bottom-right"
      offset={16}
      mobileOffset={16}
      gap={8}
      visibleToasts={3}
      containerAriaLabel={label}
      toastOptions={{ unstyled: true, className: "b-toast-slot" }}
    />
  );
}
